// `label` + `description` are the canonical English text shown to reviewers and the
// universal fallback where i18n isn't wired (e.g. the shared DocumentViewer / manager app).
// Apps with i18n (admin) override these per-locale via the `documents` namespace, using these
// as the `t()` default value — so this object is the single English source of truth.
export const DOCUMENT_FLAGS = {
	EXPIRED: {
		rejectsDocument: true,
		category: 'validity',
		sentiment: 'negative',
		label: 'Expired',
		description: "The document's validity date has passed.",
	},
	TYPE_MISMATCH: {
		rejectsDocument: true,
		category: 'identity',
		sentiment: 'negative',
		label: 'Wrong type',
		description: 'This document type is not in the list of allowed types.',
	},
	NAME_MISMATCH: {
		rejectsDocument: true,
		category: 'identity',
		sentiment: 'negative',
		label: 'Name mismatch',
		description: 'The name on the document does not match the provided name.',
	},
	DOB_MISMATCH: {
		rejectsDocument: true,
		category: 'identity',
		sentiment: 'negative',
		label: 'Date of birth mismatch',
		description: 'The date of birth on the document does not match the provided date.',
	},
	FORGERY_SUSPECTED: {
		rejectsDocument: true,
		category: 'integrity',
		sentiment: 'negative',
		label: 'Forgery suspected',
		description: 'Visual evidence suggests the document may be forged or altered.',
	},
	MISSING_REQUIRED_FIELDS: {
		rejectsDocument: true,
		category: 'completeness',
		sentiment: 'negative',
		label: 'Missing required fields',
		description: 'One or more required fields are absent from the document.',
	},
	INCOMPLETE_DOCUMENT: {
		rejectsDocument: true,
		category: 'completeness',
		sentiment: 'negative',
		label: 'Incomplete document',
		description: 'The document appears incomplete; expected pages or sections are missing.',
	},
	MISSING_FRONT_SIDE: {
		rejectsDocument: true,
		category: 'completeness',
		sentiment: 'negative',
		label: 'Missing front side',
		description: 'The front side of the identity document is missing from the upload.',
	},
	MISSING_BACK_SIDE: {
		rejectsDocument: true,
		category: 'completeness',
		sentiment: 'negative',
		label: 'Missing back side',
		description: 'The back side of the identity document is missing from the upload.',
	},
	CDR_VERIFIED: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'positive',
		label: 'CDR verified',
		description: 'The diploma number was found and verified in the CDR registry.',
	},
	CDR_MISMATCH: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'negative',
		label: 'CDR mismatch',
		description: 'Diploma number found in CDR but one or more fields do not match.',
	},
	CDR_NOT_FOUND: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'negative',
		label: 'Not in CDR',
		description: 'The diploma number was not found in the CDR registry.',
	},
	LIMOSA_VERIFIED: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'positive',
		label: 'Limosa verified',
		description: 'The Limosa QR code was verified against the official page.',
	},
	LIMOSA_MISMATCH: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'negative',
		label: 'Limosa mismatch',
		description: 'Limosa QR found but fields do not match the document.',
	},
	LIMOSA_NOT_FOUND: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'negative',
		label: 'Limosa not found',
		description: 'The Limosa QR code could not be verified.',
	},
	COMPANY_INFO_MISMATCH: {
		rejectsDocument: false,
		category: 'identity',
		sentiment: 'negative',
		label: 'Company info mismatch',
		description: 'Company name or address on document differs from provided data.',
	},
	EXTERNAL_VERIFICATION_FAILED: {
		rejectsDocument: false,
		category: 'verification',
		sentiment: 'negative',
		label: 'External check failed',
		description: 'Automated verification against an external registry was inconclusive.',
	},
	MISSING_STAMP_OR_SIGNATURE: {
		rejectsDocument: false,
		category: 'integrity',
		sentiment: 'negative',
		label: 'Missing stamp/signature',
		description: 'An expected official stamp or signature is absent.',
	},
	BLURRY: {
		rejectsDocument: false,
		category: 'quality',
		sentiment: 'negative',
		label: 'Blurry',
		description: 'Image quality is too low for reliable verification.',
	},
	ENCRYPTED: {
		rejectsDocument: false,
		category: 'quality',
		sentiment: 'negative',
		label: 'Encrypted',
		description: 'The PDF file is password-protected.',
	},
	BLANK_DOCUMENT: {
		rejectsDocument: false,
		category: 'quality',
		sentiment: 'negative',
		label: 'Blank document',
		description: 'The document contains no visible text or images.',
	},
	TOO_MANY_PAGES: {
		rejectsDocument: false,
		category: 'quality',
		sentiment: 'negative',
		label: 'Too many pages',
		description: 'The document exceeds the maximum allowed page count.',
	},
	// Informational, not a problem: the document type always routes to manual review
	// (defaultInReview). Surfaced to reviewers as the reason, but never blocks the worker.
	DEFAULT_IN_REVIEW: {
		rejectsDocument: false,
		category: 'policy',
		sentiment: 'info',
		label: 'Manual review required',
		description: 'This document type is always reviewed manually before it can be accepted.',
	},
} as const satisfies Record<
	string,
	{ rejectsDocument: boolean; category: string; sentiment: 'positive' | 'negative' | 'info'; label: string; description: string }
>;

export type DocumentFlag = keyof typeof DOCUMENT_FLAGS;

export const REJECTION_FLAGS = Object.entries(DOCUMENT_FLAGS)
	.filter(([, meta]) => meta.rejectsDocument)
	.map(([key]) => key as DocumentFlag);

// Flags that represent a passed extra check (shown green); everything else is treated as a problem (red).
export const POSITIVE_FLAGS = Object.entries(DOCUMENT_FLAGS)
	.filter(([, meta]) => meta.sentiment === 'positive')
	.map(([key]) => key as DocumentFlag);
